import React from 'react'
import {
    Breakpoints,
    CTA,
    Image,
    Layout,
    LayoutRowGap,
    Text,
    TextTag,
    TokenTextAppearance,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'
import {
    eventInfoCommon,
    TrackingAction,
    trackingEvent,
} from '../../shared/trackingServiceHelper'
import {ConfigurationPromo} from '../../types/configurationPromo'
import {stripTags} from '../../shared/utilities'

const PromotionsContainer = styled.ul`
    display: grid;
    align-items: start;
    gap: ${(props) => props.theme.size.static520};
    row-gap: ${(props) => props.theme.size.static600};
    grid-template-columns: 1fr;
    margin: 0;
    padding: 0;

    @media (min-width: ${Breakpoints.b560}px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: ${Breakpoints.b960}px) {
        grid-template-columns: repeat(3, 1fr);
    }
`
const PromotionsItemContainer = styled.li`
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.theme.size.static350};
    max-width: 840px;
    margin: auto;
    height: 100%;
`
const OnePromotionContainer = styled.div`
    max-width: 840px;
    margin: auto;
`

const PromotionDetailsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.theme.size.static350};
`
// Not a link on purpose: the CTA has the same destination and is the only link of
// the promotion (no redundant links); the image click is just a bigger target for
// mouse users
const ImageContainer = styled.div`
    overflow: hidden;
    cursor: pointer;
    &:hover {
        img {
            transform: scale(1.03);
        }
    }
`
const ImageWithFocalPoint = styled(Image)`
    height: 100%;
    max-width: 100%;
    object-fit: cover;
    object-position: 50% 50%;
    transition: transform 2.2s cubic-bezier(0.14, 1.12, 0.67, 0.99) 0s;
`
type PromotionHeadingTag = TextTag.h2 | TextTag.h3

interface PromotionsComponentProps {
    promotions: ConfigurationPromo[]
    headingTag?: PromotionHeadingTag
}
interface PromotionItemProps {
    promotion: ConfigurationPromo
}
interface PromotionDetailsProps extends PromotionItemProps {
    headingTag: PromotionHeadingTag
}

const PromotionImage: React.FC<PromotionItemProps> = ({promotion}) => {
    if (!promotion?.teaser) {
        return null
    }

    const openPromotionFromImage = (e: React.MouseEvent) => {
        const url = promotion.teaser.cta_url

        trackingEvent(TrackingAction.VW_BASIC_TEASER_CLICK, {
            eventInfo: {
                ...eventInfoCommon(),
                contentId: promotion.teaser.cta_label,
            },
            link: {
                name: promotion.teaser.cta_label,
                url,
                newTab: false,
            },
        })

        if (!url) {
            return
        }

        if (e.ctrlKey || e.metaKey) {
            window.open(url, '_blank', 'noopener')

            return
        }

        window.location.assign(url)
    }

    return (
        <ImageContainer onClick={openPromotionFromImage}>
            <picture>
                <source srcSet={promotion.teaser.img || ''} />
                <ImageWithFocalPoint
                    src={promotion.teaser.img || ''}
                    alt={stripTags(promotion.teaser.title)}
                    loading="lazy"
                />
            </picture>
        </ImageContainer>
    )
}

const PromotionDetails: React.FC<PromotionDetailsProps> = ({
    promotion,
    headingTag,
}) => {
    if (!promotion?.teaser) {
        return null
    }

    return (
        <PromotionDetailsContainer>
            <Text tag={headingTag} appearance={TokenTextAppearance.headline300}>
                <span
                    dangerouslySetInnerHTML={{
                        __html: promotion.teaser.title || '',
                    }}
                />
            </Text>
            <span
                dangerouslySetInnerHTML={{
                    __html: promotion.teaser.description || '',
                }}
            />
            <CTA
                tag="a"
                emphasis="secondary"
                href={promotion.teaser.cta_url || ''}
                title={promotion.teaser.cta_label || ''}
                aria-label={promotion.teaser.cta_label || ''}
                onClick={() =>
                    trackingEvent(TrackingAction.VW_BASIC_BUTTON_CLICK, {
                        eventInfo: {
                            ...eventInfoCommon(),
                            contentId: promotion.teaser.cta_label,
                        },
                        link: {
                            name: promotion.teaser.cta_label,
                            url: promotion.teaser.cta_url,
                            newTab: false,
                        },
                    })
                }
            >
                {promotion.teaser.cta_label || ''}
            </CTA>
        </PromotionDetailsContainer>
    )
}

export const PromotionsComponent: React.FC<PromotionsComponentProps> = ({
    promotions,
    headingTag = TextTag.h2,
}) => {
    const singlePromotionLayout = {
        [Breakpoints.default]: [
            {name: 'a', columns: 24},
            {name: 'b', columns: 24},
        ],
        [Breakpoints.b560]: [
            {name: 'a', columns: 11},
            {name: '.', columns: 2},
            {name: 'b', columns: 11},
        ],
    }

    return (
        <>
            <>
                {promotions.length > 1 ? (
                    <PromotionsContainer>
                        {promotions.map((promotion, index) => (
                            <PromotionsItemContainer key={index}>
                                <PromotionImage promotion={promotion} />
                                <PromotionDetails
                                    promotion={promotion}
                                    headingTag={headingTag}
                                />
                            </PromotionsItemContainer>
                        ))}
                    </PromotionsContainer>
                ) : (
                    promotions.length === 1 && (
                        <OnePromotionContainer>
                            <Layout
                                appearance={singlePromotionLayout}
                                rowGap={LayoutRowGap.static350}
                            >
                                <PromotionImage promotion={promotions[0]} />
                                <PromotionDetails
                                    promotion={promotions[0]}
                                    headingTag={headingTag}
                                />
                            </Layout>
                        </OnePromotionContainer>
                    )
                )}
            </>
        </>
    )
}
